import { Mutex } from 'async-mutex';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';

import { type RootState } from 'store';
import { actions as AppActions } from 'store/app/reducer';
import { actions as SettingsActions } from 'store/settings/reducer';

import { serialize } from './paramsSerializer';

const mutex = new Mutex();
const baseQuery = fetchBaseQuery({
  baseUrl: '/api',
  paramsSerializer: serialize,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).settings.access_token;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
});
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  console.log(result);

  // if server returns 503 status we have to force a page reload
  // to display the maintenance page
  if (result.meta?.response?.status === 503) {
    window.location.reload();
    return result;
  }

  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const refreshToken = (api.getState() as RootState).settings
          .refresh_token;

        if (!refreshToken) {
          api.dispatch(AppActions.logout());
        } else {
          const refreshResult = await baseQuery(
            {
              method: 'POST',
              url: `${window.location.origin}/token/refresh`,
              body: {
                refreshToken,
              },
            },
            api,
            extraOptions
          );

          if (refreshResult.data && typeof refreshResult.data === 'string') {
            const refreshTokenData = JSON.parse(refreshResult.data);

            if (typeof refreshTokenData === 'object') {
              if (
                'error' in refreshTokenData &&
                refreshTokenData.error === 'invalid_grant'
              ) {
                api.dispatch(
                  AppActions.logout(
                    'Istunto on vanhentunut! Ole hyvä ja kirjaudu sisään uudelleen.'
                  )
                );
              } else {
                let payload: Record<string, unknown> = {
                  expires_in_start: null,
                  expires_in: null,
                  access_token: null,
                  token_type: null,
                };

                for (let key in refreshTokenData) {
                  if (key === 'access_token') {
                    payload['expires_in_start'] = Math.floor(Date.now() / 1000);
                  }
                  payload[key] = refreshTokenData[key];
                }

                api.dispatch(SettingsActions.setAuthorizationSettings(payload));

                result = await baseQuery(args, api, extraOptions);
              }
            }
          } else {
            api.dispatch(AppActions.logout());
          }
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};
